import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ParentTopic {
  parentTopicSlug: string;
  label: string;
  childTopicSlugs: string[];
  /** Pages mentioning each child slug (distinct analyses) — compact topics API only */
  childTopicPageCounts?: Record<string, number>;
  /** Pages mentioning the parent slug as a signal — compact topics API only */
  parentTopicPageCount?: number;
  pageCount: number;
  effectivePageCountForCoverage?: number;
  totalWeight: number;
  averageWeight: number;
  coverageScore: number;
  breadthScore: number;
  depthScore: number;
  strengthScore: number;
  focusScore: number;
  authorityScore: number;
  // Enhanced coverage fields (optional)
  enhancedPageCountForCoverage?: number;
  relatedTopicsContribution?: number;
  relatedTopicsBreakdown?: Array<{
    topicSlug: string;
    similarity: number;
    pageCount: number;
    contribution: number;
  }>;
  // Link metrics (Phase 2 - optional, only if links enabled)
  contextualLinkCount?: number;
  structuralLinkCount?: number;
  linkScore?: number;
  // Explanation fields
  authorityExplanation?: {
    coverage?: string;
    strength?: string;
    focus?: string;
    links?: string;
  };
  // Topic classification
  topicTier?: 'owned' | 'aspirational' | 'ambient' | 'adjacent' | 'anti' | 'unclassified';
  topicTierReasoning?: string[];
}

export interface DomainTopicsResponse {
  success: boolean;
  data: {
    domain: string;
    parentTopics: ParentTopic[];
    message?: string;
  };
  timestamp: string;
}

export interface TopicLink {
  sourceUrl: string;
  sourcePageId: string;
  targetUrl: string;
  targetPageId: string;
  anchorText: string;
  linkType: string;
  confidence: number;
}

export interface TopicLinksResponse {
  success: boolean;
  data: {
    topic: string;
    domain: string;
    contextualLinks: TopicLink[];
    structuralLinks: TopicLink[];
    message?: string;
  };
  timestamp: string;
}

export interface TopicGraphNode {
  id: string;
  label: string;
  type: 'parentTopic' | 'grandparentTopic';
  grandparentTopic?: string;
  pathToGrandparent?: string[]; // Intermediate topics connecting this topic to its grandparent
  pageCount: number;
  authorityScore: number;
  exists: boolean;
}

export interface TopicGraphEdge {
  source: string;
  target: string;
  similarity: number;
  type: 'merged' | 'related' | 'somewhat-related';
  transitivePath?: string[]; // Intermediate topics that connect source and target
}

export interface TopicGraphResponse {
  success: boolean;
  data: {
    nodes: TopicGraphNode[];
    edges: TopicGraphEdge[];
  };
  timestamp: string;
}

export interface TopicOverviewNode {
  topicSlug: string;
  label: string;
  authorityScore: number;
  topicTier?: string;
  pageCount: number;
  community?: string;
  samplePages: Array<{
    url: string;
    pageId: string;
    weight: number;
  }>;
}

export interface TopicOverviewEdge {
  sourceTopicSlug: string;
  targetTopicSlug: string;
  edgeType: 'merged' | 'related' | 'somewhat-related';
  confidence: number;
  similarity: number;
  coOccurrenceScore: number;
  linkCoSupportScore: number;
  supportingPageCount: number;
  evidencePages: Array<{
    pageId: string;
    url: string;
    sourceTopicWeight?: number;
    targetTopicWeight?: number;
  }>;
}

export interface TopicOverviewResponse {
  success: boolean;
  data: {
    domain: string;
    runId: string | null;
    filters: {
      topN: number;
      minAuthority: number;
      minConfidence: number;
      maxEdgesPerNode: number;
      search?: string;
    };
    nodes: TopicOverviewNode[];
    edges: TopicOverviewEdge[];
  };
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class DomainTopicsService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  /**
   * Get all parentTopics for a domain
   */
  getDomainTopics(domain: string, options?: { compact?: boolean }): Observable<DomainTopicsResponse> {
    const normalizedDomain = domain.toLowerCase().trim();
    const params = options?.compact ? { compact: '1' } : undefined;
    return this.http.get<DomainTopicsResponse>(`${this.API_URL}/domains/${normalizedDomain}/topics`, {
      params
    });
  }

  /**
   * Get link details for a specific topic
   * Phase 2: Internal Links
   */
  getTopicLinks(domain: string, topicSlug: string): Observable<TopicLinksResponse> {
    const normalizedDomain = domain.toLowerCase().trim();
    const normalizedTopicSlug = topicSlug.toLowerCase().trim();
    return this.http.get<TopicLinksResponse>(`${this.API_URL}/domains/${normalizedDomain}/topics/${normalizedTopicSlug}/links`);
  }

  /**
   * Get topic graph showing relationships
   */
  getTopicGraph(domain: string, minSimilarity: number = 0.65): Observable<TopicGraphResponse> {
    const normalizedDomain = domain.toLowerCase().trim();
    return this.http.get<TopicGraphResponse>(`${this.API_URL}/domains/${normalizedDomain}/topics/graph`, {
      params: { minSimilarity: minSimilarity.toString() }
    });
  }

  getTopicOverview(
    domain: string,
    options?: {
      topN?: number;
      minAuthority?: number;
      minConfidence?: number;
      maxEdgesPerNode?: number;
      search?: string;
    }
  ): Observable<TopicOverviewResponse> {
    const normalizedDomain = domain.toLowerCase().trim();
    const params: Record<string, string> = {};
    if (typeof options?.topN === 'number') params['topN'] = `${options.topN}`;
    if (typeof options?.minAuthority === 'number') params['minAuthority'] = `${options.minAuthority}`;
    if (typeof options?.minConfidence === 'number') params['minConfidence'] = `${options.minConfidence}`;
    if (typeof options?.maxEdgesPerNode === 'number') params['maxEdgesPerNode'] = `${options.maxEdgesPerNode}`;
    if (options?.search) params['search'] = options.search;

    return this.http.get<TopicOverviewResponse>(`${this.API_URL}/domains/${normalizedDomain}/topics/overview`, {
      params
    });
  }

  /**
   * Get relationship tree for a specific topic
   */
  getTopicTree(domain: string, topicSlug: string, maxLevels: number = 3, maxPerLevel: number = 5): Observable<TopicTreeResponse> {
    const normalizedDomain = domain.toLowerCase().trim();
    const normalizedTopicSlug = topicSlug.toLowerCase().trim();
    return this.http.get<TopicTreeResponse>(`${this.API_URL}/domains/${normalizedDomain}/topics/${normalizedTopicSlug}/tree`, {
      params: { 
        maxLevels: maxLevels.toString(),
        maxPerLevel: maxPerLevel.toString()
      }
    });
  }

  /**
   * Get diagnostic data for a specific topic
   */
  getTopicDiagnostic(domain: string, topicSlug: string): Observable<DiagnosticResponse> {
    const normalizedDomain = domain.toLowerCase().trim();
    const normalizedTopicSlug = topicSlug.toLowerCase().trim();
    return this.http.get<DiagnosticResponse>(`${this.API_URL}/domains/${normalizedDomain}/topics/${normalizedTopicSlug}/diagnostic`);
  }
}

export interface TopicTreeNode {
  topicSlug: string;
  label: string;
  authorityScore: number;
  pageCount: number;
  relationshipType?: 'grandparent' | 'bridge' | 'merged' | 'related' | 'somewhat-related';
  similarity?: number;
  path?: string[];
  children?: TopicTreeNode[];
}

export interface TopicTreeResponse {
  success: boolean;
  data: {
    topic: {
      topicSlug: string;
      label: string;
      authorityScore: number;
      pageCount: number;
      grandparent?: string;
    };
    upLevels: TopicTreeNode[][];
    downLevels: TopicTreeNode[][];
    pathMetrics?: {
      pathLength: number;
      averageSimilarity?: number;
      weakestLink?: number;
      directSimilarityToGrandparent?: number;
    };
  };
  timestamp: string;
}

export interface DiagnosticTopicUrl {
  url: string;
  pageId: string;
  weight: number;
  salience: number;
  relativeDensity: number;
  seoIntentScore: number;
  intentBucket: 'core' | 'mention';
  specificityScore: number;
  marginToRunnerUp: number;
  contrastScore: number;
  ownershipScore: number;
  topicPrevalence: number;
  ubiquityPenalty: number;
  isAmbientLike: boolean;
  rankedKeywordCount: number;
  topicKeywordMatches: number;
  bestPosition: number | null;
  matchedSearchVolume: number;
  trafficProxy: number;
  estimatedOrganicTraffic?: number;
  topicTags?: Array<{
    topicSlug: string;
    label: string;
    weight: number;
  }>;
}

export interface DiagnosticResponse {
  success: boolean;
  data: {
    centerTopic: {
      parentTopicSlug: string;
      label: string;
      authorityScore: number;
      topicTier?: string;
      pageCount: number;
      coverageScore: number;
      breadthScore: number;
      depthScore: number;
      strengthScore: number;
      focusScore: number;
      linkScore?: number;
    };
    coverage: {
      directlyCoveredTopics: Array<{
        parentTopicSlug: string;
        label: string;
        pageCount: number;
        authorityScore: number;
        urlCount: number;
        coreCount: number;
        mentionCount: number;
        corePages: DiagnosticTopicUrl[];
        mentionPages: DiagnosticTopicUrl[];
        topUrls: DiagnosticTopicUrl[]; // Compatibility alias
        hasMoreCore: boolean;
        hasMoreMentions: boolean;
        hasMore: boolean;
      }>;
      strongestPages: Array<{
        url: string;
        pageId: string;
        weight: number;
        salience: number;
        seoIntentScore: number;
        topicTags: Array<{
          topicSlug: string;
          label: string;
          weight: number;
        }>;
      }>;
      relatedTopics: Array<{
        parentTopicSlug: string;
        label: string;
        similarity: number;
        pageCount: number;
        contribution: number;
        urlCount: number;
        coreCount: number;
        mentionCount: number;
        corePages: DiagnosticTopicUrl[];
        mentionPages: DiagnosticTopicUrl[];
        topUrls: DiagnosticTopicUrl[]; // Compatibility alias
        hasMoreCore: boolean;
        hasMoreMentions: boolean;
        hasMore: boolean;
      }>;
    };
    strength: {
      totalWeight: number;
      averageWeight: number;
      strengthScore: number;
      topPagesByWeight: Array<{
        url: string;
        pageId: string;
        weight: number;
        salience: number;
        seoIntentScore: number;
      }>;
    };
    focus: {
      averageWeight: number;
      focusScore: number;
      mostFocusedPages: Array<{
        url: string;
        pageId: string;
        weight: number;
        salience: number;
        relativeDensity: number;
      }>;
    };
    links: {
      contextualLinkCount?: number;
      structuralLinkCount?: number;
      linkScore?: number;
      topContextualLinks: Array<{
        sourceUrl: string;
        targetUrl: string;
        anchorText: string;
        linkType: string;
      }>;
    };
  };
  timestamp: string;
}

